/**
 * AppShell Component - Main application layout with navigation
 * @module components/starnexus/common/AppShell
 *
 */

import React, { useState, useCallback, useEffect } from 'react';
import AppLayout from '@amzn/awsui-components-console/app-layout';
import Flashbar from '@amzn/awsui-components-console/flashbar';
import TopNavigation from '@amzn/awsui-components-console/top-navigation';
import { Navigation } from './Navigation';
import { ConfirmationModal } from './ConfirmationModal';
import { MobileWarning } from './MobileWarning';
import { AIAssistantContent } from './AIAssistantDrawer';
import '../../../styles/chat.css';
import { useUIStore, useFlashMessages } from '../../../stores/starnexus/uiStore';
import { useAuthStore } from '../../../stores/starnexus/authStore';
import { useResponsive } from '../../../hooks/starnexus/useResponsive';
import { useNavigate } from 'react-router-dom';

// ============================================================================
// AppShell Props
// ============================================================================

export interface AppShellProps {
  children: React.ReactNode;
  /** Optional breadcrumbs component */
  breadcrumbs?: React.ReactNode;
  /** Content type for AppLayout optimization */
  contentType?: 'default' | 'table' | 'form' | 'cards' | 'wizard';
  /** Disable padding in content area */
  disableContentPaddings?: boolean;
  /** Tools panel content */
  tools?: React.ReactNode;
  /** Whether tools panel is open */
  toolsOpen?: boolean;
  /** Callback when tools panel state changes */
  onToolsChange?: (open: boolean) => void;
}

// ============================================================================
// AppShell Component
// ============================================================================

export const AppShell: React.FC<AppShellProps> = ({ children, breadcrumbs, contentType = 'default', disableContentPaddings = false, tools: _tools, toolsOpen = false, onToolsChange: _onToolsChange }) => {
  console.log('[AppShell] Rendering');
  const { shouldCollapseSidebar, isMobile } = useResponsive();
  const [navigationOpen, setNavigationOpen] = useState(!shouldCollapseSidebar);
  const [_localToolsOpen, _setLocalToolsOpen] = useState(toolsOpen);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  const flashMessages = useFlashMessages();
  const removeFlashMessage = useUIStore((state) => state.removeFlashMessage);
  const alias = useAuthStore((state) => state.alias);
  const navigate = useNavigate();

  // Update navigation state when screen size changes
  useEffect(() => {
    if (shouldCollapseSidebar) {
      setNavigationOpen(false);
    }
  }, [shouldCollapseSidebar]);

  console.log('[AppShell] Alias:', alias);

  // Convert flash messages to Flashbar items
  const flashbarItems = React.useMemo(
    () =>
      flashMessages.map((msg) => ({
        type: msg.type,
        header: msg.header,
        content: msg.content,
        dismissible: msg.dismissible,
        dismissLabel: 'Dismiss message',
        onDismiss: () => removeFlashMessage(msg.id),
        id: msg.id
      })),
    [flashMessages, removeFlashMessage]
  );

  const handleUtilityClick = useCallback(
    (event: CustomEvent<{ id: string }>) => {
      const { id } = event.detail;
      switch (id) {
        case 'signout':
          // Midway handles authentication — navigate to home
          navigate('/starnexus');
          break;
        case 'settings':
          navigate('/starnexus/settings');
          break;
      }
    },
    [navigate]
  );

  return (
    <>
      <div id="h" style={{ position: 'sticky', top: 0, zIndex: 1002 }}>
        <TopNavigation
          identity={{
            href: '/starnexus',
            title: 'StarNexus',
            onFollow: (e) => {
              e.preventDefault();
              navigate('/starnexus');
            }
          }}
          utilities={[
            {
              type: 'button',
              iconName: 'notification',
              title: 'Notifications',
              ariaLabel: 'Notifications',
              badge: false,
              disableUtilityCollapse: false
            },
            {
              type: 'menu-dropdown',
              text: alias || 'User',
              description: alias ? `${alias}@amazon.com` : '',
              iconName: 'user-profile',
              onItemClick: handleUtilityClick,
              items: [
                { id: 'settings', text: 'Settings' },
                { id: 'signout', text: 'Sign out' }
              ]
            }
          ]}
          i18nStrings={{
            overflowMenuTriggerText: 'More',
            overflowMenuTitleText: 'All'
          }}
        />
      </div>
      <AppLayout
        headerSelector="#h"
        navigation={<Navigation />}
        navigationOpen={navigationOpen}
        onNavigationChange={(event) => setNavigationOpen(event.detail.open)}
        notifications={flashbarItems.length > 0 ? <Flashbar items={flashbarItems} /> : undefined}
        breadcrumbs={breadcrumbs}
        content={children}
        contentType={contentType}
        disableContentPaddings={disableContentPaddings}
        drawers={[{
          id: 'ai-assistant',
          ariaLabels: {
            drawerName: 'AI Assistant',
            closeButton: 'Close AI Assistant',
            triggerButton: 'Open AI Assistant',
            resizeHandle: 'Resize AI Assistant',
          },
          trigger: { iconName: 'gen-ai' },
          content: <AIAssistantContent />,
          resizable: true,
          defaultSize: 420,
        }]}
        activeDrawerId={aiDrawerOpen ? 'ai-assistant' : null}
        onDrawerChange={(event) => setAiDrawerOpen(event.detail.activeDrawerId === 'ai-assistant')}
        ariaLabels={{
          navigation: 'Side navigation',
          navigationToggle: 'Open navigation',
          navigationClose: 'Close navigation',
          notifications: 'Notifications',
          tools: 'Help panel',
          toolsToggle: 'Open help panel',
          toolsClose: 'Close help panel',
        }}
      />
      <ConfirmationModal />
      {/* Mobile warning banner - shown on screens < 768px */}
      <MobileWarning show={isMobile} />
    </>
  );
};

export default AppShell;
